import React from 'react';

/**
 * TrendChart — Simple SVG sparkline/bar chart for trend data.
 *
 * Props:
 *   data: Array<{ date: string, value: number }>
 *   height: number (px)
 *   color: string (hex)
 *   type: 'line' | 'bar'
 */

const TrendChart = ({ data = [], height = 80, color = '#6366f1', type = 'line' }) => {
    if (!data.length) {
        return (
            <div style={{
                height, display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#94a3b8', fontSize: 12,
            }}>
                No trend data yet
            </div>
        );
    }

    const values = data.map(d => d.value);
    const max = Math.max(...values, 1);
    const min = Math.min(...values, 0);
    const range = max - min || 1;
    const width = 300;
    const padding = 4;
    const chartH = height - padding * 2;
    const chartW = width - padding * 2;

    if (type === 'bar') {
        const barWidth = Math.max(2, (chartW / data.length) - 2);
        const barGap = 2;

        return (
            <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
                {data.map((d, i) => {
                    const barH = ((d.value - min) / range) * chartH;
                    const x = padding + i * (barWidth + barGap);
                    const y = padding + chartH - barH;
                    return (
                        <rect
                            key={i}
                            x={x} y={y}
                            width={barWidth} height={Math.max(barH, 1)}
                            rx={2}
                            fill={color}
                            opacity={0.7 + (i / data.length) * 0.3}
                        />
                    );
                })}
            </svg>
        );
    }

    // Line chart
    const points = data.map((d, i) => {
        const x = padding + (i / Math.max(data.length - 1, 1)) * chartW;
        const y = padding + chartH - ((d.value - min) / range) * chartH;
        return `${x},${y}`;
    });
    const pathD = `M ${points.join(' L ')}`;

    // Area fill
    const firstX = padding;
    const lastX = padding + chartW;
    const areaD = `${pathD} L ${lastX},${padding + chartH} L ${firstX},${padding + chartH} Z`;

    return (
        <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
            <defs>
                <linearGradient id={`trend-fill-${color.replace('#', '')}`} x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity="0.15" />
                    <stop offset="100%" stopColor={color} stopOpacity="0.02" />
                </linearGradient>
            </defs>
            <path
                d={areaD}
                fill={`url(#trend-fill-${color.replace('#', '')})`}
            />
            <path
                d={pathD}
                fill="none" stroke={color} strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round"
            />
            {/* End dot */}
            {data.length > 0 && (() => {
                const lastI = data.length - 1;
                const cx = padding + (lastI / Math.max(data.length - 1, 1)) * chartW;
                const cy = padding + chartH - ((data[lastI].value - min) / range) * chartH;
                return <circle cx={cx} cy={cy} r={3} fill={color} />;
            })()}
        </svg>
    );
};

export default TrendChart;
